import { User, UserRole, AuditLog } from '../types';
import { auditService } from './auditService';

// Service de gestion des utilisateurs
const mockUsers: (User & { password: string; isActive: boolean })[] = [
  {
    id: '1',
    email: 'admin@herminette.fr', 
    password: 'admin123',
    firstName: 'Younes',
    lastName: 'A.',
    role: 'admin',
    permissions: [],
    isActive: true,
    createdAt: '2024-01-01T00:00:00Z'
  },
  {
    id: '2',
    email: 'bureau@herminette.fr',
    password: 'bureau123',
    firstName: 'Louis',
    lastName: 'D.',
    role: 'responsable_bureau',
    permissions: [],
    isActive: true,
    createdAt: '2024-01-01T00:00:00Z'
  },
  {
    id: '3',
    email: 'equipe1@herminette.fr',
    password: 'equipe123',
    firstName: 'Thomas',
    lastName: 'A.',
    role: 'responsable_equipe',
    teamId: 'team-5',
    permissions: [],
    isActive: true,
    createdAt: '2024-01-01T00:00:00Z'
  },
  {
    id: '4',
    email: 'charpentier@herminette.fr',
    password: 'charpentier123',
    firstName: 'Luc',
    lastName: 'T.',
    role: 'charpentier',
    teamId: 'team-2',
    permissions: [],
    isActive: true,
    createdAt: '2024-01-01T00:00:00Z'
  },
  {
    id: '5',
    email: 'stagiaire@herminette.fr',
    password: 'stagiaire123',
    firstName: 'Yannis',
    lastName: 'D.',
    role: 'stagiaire',
    teamId: 'team-1',
    permissions: [],
    isActive: true,
    createdAt: '2024-01-01T00:00:00Z'
  }
];

class UserService {
  async getUsers(filters?: {
    search?: string;
    role?: UserRole;
    isActive?: boolean;
  }): Promise<(User & { isActive: boolean })[]> {
    await new Promise(resolve => setTimeout(resolve, 300));
    
    let filtered = mockUsers.map(({ password, ...user }) => user);
    
    if (filters?.search) {
      const search = filters.search.toLowerCase();
      filtered = filtered.filter(u => 
        u.firstName.toLowerCase().includes(search) ||
        u.lastName.toLowerCase().includes(search) ||
        u.email.toLowerCase().includes(search)
      );
    }
    
    if (filters?.role) {
      filtered = filtered.filter(u => u.role === filters.role);
    }
    
    if (filters?.isActive !== undefined) {
      filtered = filtered.filter(u => u.isActive === filters.isActive);
    }
    
    return filtered.sort((a, b) => a.lastName.localeCompare(b.lastName));
  }

  async getUser(id: string): Promise<(User & { isActive: boolean }) | null> {
    await new Promise(resolve => setTimeout(resolve, 200));
    const user = mockUsers.find(u => u.id === id);
    if (!user) return null;
    
    const { password, ...userData } = user;
    return userData;
  }

  async createUser(userData: {
    email: string;
    firstName: string;
    lastName: string;
    role: UserRole;
    teamId?: string;
    password: string;
  }, createdBy: string): Promise<User & { isActive: boolean }> {
    await new Promise(resolve => setTimeout(resolve, 500));
    
    // Vérifier si l'email existe déjà
    if (mockUsers.some(u => u.email === userData.email)) {
      throw new Error('Un utilisateur avec cet email existe déjà');
    }
    
    const newUser = {
      id: `user-${Date.now()}`,
      ...userData,
      permissions: [],
      isActive: true,
      createdAt: new Date().toISOString()
    };
    
    mockUsers.push(newUser);
    
    // Log de l'action
    await auditService.logAction(
      createdBy,
      'admin',
      'CREATE_USER',
      'user',
      newUser.id,
      { email: userData.email, role: userData.role },
      `Création de l'utilisateur ${userData.firstName} ${userData.lastName}`
    );
    
    const { password, ...result } = newUser;
    return result;
  }

  async updateUser(id: string, updates: {
    email?: string;
    firstName?: string;
    lastName?: string;
    role?: UserRole;
    teamId?: string;
  }, updatedBy: string): Promise<User & { isActive: boolean }> {
    await new Promise(resolve => setTimeout(resolve, 500));
    
    const userIndex = mockUsers.findIndex(u => u.id === id);
    if (userIndex === -1) {
      throw new Error('Utilisateur non trouvé');
    }
    
    // Vérifier si le nouvel email existe déjà (si changé)
    if (updates.email && updates.email !== mockUsers[userIndex].email) {
      if (mockUsers.some(u => u.email === updates.email && u.id !== id)) {
        throw new Error('Un utilisateur avec cet email existe déjà');
      }
    }
    
    const oldData = { ...mockUsers[userIndex] };
    mockUsers[userIndex] = {
      ...mockUsers[userIndex],
      ...updates
    };
    
    // Log de l'action
    await auditService.logAction(
      updatedBy,
      'admin',
      'UPDATE_USER',
      'user',
      id,
      updates,
      `Modification de l'utilisateur ${mockUsers[userIndex].firstName} ${mockUsers[userIndex].lastName}`
    );
    
    const { password, ...result } = mockUsers[userIndex];
    return result;
  }

  async deleteUser(id: string, deletedBy: string): Promise<void> {
    await new Promise(resolve => setTimeout(resolve, 300));
    
    const userIndex = mockUsers.findIndex(u => u.id === id);
    if (userIndex === -1) {
      throw new Error('Utilisateur non trouvé');
    }
    
    const user = mockUsers[userIndex];
    
    // Ne pas permettre la suppression de l'admin principal
    if (user.role === 'admin' && user.email === 'admin@herminette.fr') {
      throw new Error('Impossible de supprimer l\'administrateur principal');
    }
    
    mockUsers.splice(userIndex, 1);
    
    // Log de l'action
    await auditService.logAction(
      deletedBy,
      'admin',
      'DELETE_USER',
      'user',
      id,
      { email: user.email, role: user.role },
      `Suppression de l'utilisateur ${user.firstName} ${user.lastName}`
    );
  }

  async toggleUserStatus(id: string, isActive: boolean, updatedBy: string): Promise<void> {
    await new Promise(resolve => setTimeout(resolve, 300));
    
    const userIndex = mockUsers.findIndex(u => u.id === id);
    if (userIndex === -1) {
      throw new Error('Utilisateur non trouvé');
    }
    
    const user = mockUsers[userIndex];
    
    // Ne pas permettre la désactivation de l'admin principal
    if (!isActive && user.role === 'admin' && user.email === 'admin@herminette.fr') {
      throw new Error('Impossible de désactiver l\'administrateur principal');
    }
    
    mockUsers[userIndex].isActive = isActive;
    
    // Log de l'action
    await auditService.logAction(
      updatedBy,
      'admin',
      isActive ? 'ACTIVATE_USER' : 'DEACTIVATE_USER',
      'user',
      id,
      { isActive },
      `${isActive ? 'Activation' : 'Désactivation'} de l'utilisateur ${user.firstName} ${user.lastName}`
    );
  }

  async resetPassword(id: string, newPassword: string, resetBy: string): Promise<void> {
    await new Promise(resolve => setTimeout(resolve, 400));
    
    const userIndex = mockUsers.findIndex(u => u.id === id);
    if (userIndex === -1) {
      throw new Error('Utilisateur non trouvé');
    }
    
    mockUsers[userIndex].password = newPassword;
    
    const user = mockUsers[userIndex];
    
    // Log de l'action
    await auditService.logAction(
      resetBy,
      'admin',
      'RESET_PASSWORD',
      'user',
      id,
      {},
      `Réinitialisation du mot de passe de ${user.firstName} ${user.lastName}`
    );
  }

  // Helper methods
  getRoleLabel(role: UserRole): string {
    const labels: Record<UserRole, string> = {
      admin: 'Administrateur',
      responsable_bureau: 'Responsable bureau',
      responsable_depot: 'Responsable dépôt',
      responsable_equipe: 'Chef d\'équipe',
      charpentier: 'Charpentier',
      stagiaire: 'Stagiaire'
    };
    return labels[role];
  }

  getRoleColor(role: UserRole): string {
    const colors: Record<UserRole, string> = {
      admin: 'bg-red-100 text-red-800',
      responsable_bureau: 'bg-blue-100 text-blue-800',
      responsable_depot: 'bg-green-100 text-green-800',
      responsable_equipe: 'bg-purple-100 text-purple-800',
      charpentier: 'bg-orange-100 text-orange-800',
      stagiaire: 'bg-gray-100 text-gray-800'
    };
    return colors[role];
  }

  getAvailableRoles(): { value: UserRole; label: string }[] {
    return [
      { value: 'charpentier', label: 'Charpentier' },
      { value: 'responsable_equipe', label: 'Chef d\'équipe' },
      { value: 'responsable_depot', label: 'Superviseur' },
      { value: 'responsable_bureau', label: 'Responsable du dépôt' }
    ];
  }

  canUserManageUsers(userRole: UserRole): boolean {
    return userRole === 'admin';
  }

  canUserViewUsers(userRole: UserRole): boolean {
    return ['admin', 'responsable_bureau', 'responsable_depot', 'responsable_equipe', 'charpentier'].includes(userRole);
  }
}

export const userService = new UserService();